import React, { useState } from "react";
import { SubPageHeader } from "../../../components/ui/SubPageHeader";
import { BadgePoundSterling, Printer, Download, Filter } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { ReportActionToolbar } from "../../../components/reports/ReportActionToolbar";
import { PaginationFooter } from "../../../components/sunbeds/PaginationFooter";
import {
  SalesTable,
  SalesItem,
} from "../../../components/reports/SalesTable";
import { ReportDateRangePicker } from "../../../components/reports/ReportDateRangePicker";
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  format,
} from "date-fns";
import { useAuth } from "../../../auth/AuthContext";

const generateMockSalesData = (currencySymbol: string): SalesItem[] => {
  const staff = ["Alice M.", "Bob D.", "Charlie R.", "Diana P.", "System"];
  const customers = ["Jane Doe", "John Smith", "Emma Wilson", "Liam Brown", "Walk-in Guest"];
  const types: ("Service" | "Product" | "Package" | "Sunbed")[] = ["Service", "Product", "Package", "Sunbed"];
  const methods = ["Cash", "Card", "Voucher", "Online"];
  const statuses: ("Completed" | "Refunded" | "Pending")[] = ["Completed", "Completed", "Completed", "Refunded", "Pending"];

  return Array.from({ length: 45 }).map((_, i) => {
    const isToday = i < 5;
    const date = subDays(new Date(), isToday ? 0 : Math.floor(i / 5));
    // Determine time: recent items later in the day
    date.setHours(9 + (i % 8), Math.floor(Math.random() * 60));

    const amountBase = Math.floor(Math.random() * 80) + 15;
    const type = types[Math.floor(Math.random() * types.length)];

    return {
      id: `sale-${i}`,
      orderNumber: `ORD-${20250000 + i}`,
      date: format(date, "dd MMM yyyy, HH:mm"),
      dateObj: date, // valuable for sorting/filtering
      customerName: customers[Math.floor(Math.random() * customers.length)],
      staffName: staff[Math.floor(Math.random() * staff.length)],
      type: type,
      paymentMethod: methods[Math.floor(Math.random() * methods.length)],
      amount: `${currencySymbol}${amountBase.toFixed(2)}`,
      status: statuses[Math.floor(Math.random() * statuses.length)],
    };
  });
};

export default function SalesPage() {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  const [sales] = useState<any[]>(() => generateMockSalesData(currencySymbol));
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [isLoading, setIsLoading] = useState(false);

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: startOfMonth(new Date()),
    end: new Date(),
  });

  // Filter logic
  const filteredSales = sales.filter((item) => {
    const matchesSearch =
      item.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.staffName.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDate = true;
    if (dateRange.start && dateRange.end && item.dateObj) {
      matchesDate =
        item.dateObj >= startOfDay(dateRange.start) &&
        item.dateObj <= endOfDay(dateRange.end);
    }

    return matchesSearch && matchesDate;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSales = filteredSales.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewSale = (sale: SalesItem) => {
    console.log("View sale details:", sale);
    // Ideally navigate to detail modal or page
  };

  return (
    <div className="flex-1 flex flex-col space-y-6 overflow-hidden p-4">
      <SubPageHeader
        title="Sales Report"
        subtitle="Detailed log of all service, product, and package sales"
        icon={BadgePoundSterling}
        actions={
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 lg:gap-3 w-full sm:w-auto">
            <div className="flex-1 sm:flex-none min-w-[140px]">
              <ReportDateRangePicker
                startDate={dateRange.start}
                endDate={dateRange.end}
                onChange={(start, end) => setDateRange({ start, end })}
              />
            </div>
            <Button
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center justify-center gap-2 h-10 px-4 sm:px-3 shadow-sm rounded-xl"
              title="Print Report"
            >
              <Printer size={18} />
              <span className="sm:hidden font-bold">Print</span>
            </Button>
          </div>
        }
      />

      <ReportActionToolbar
        totalItems={sales.length}
        showingStart={filteredSales.length > 0 ? indexOfFirstItem + 1 : 0}
        showingEnd={Math.min(indexOfLastItem, filteredSales.length)}
        searchTerm={searchTerm}
        onSearchChange={(val) => {
          setSearchTerm(val);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search by order #, customer, or staff..."
      >
        <Button
          variant="outline"
          className="h-[38px] w-[38px] p-0 flex items-center justify-center border-gray-200 text-gray-500 hover:text-[#885ABB] hover:border-[#885ABB] bg-white"
          title="Export CSV"
        >
          <Download size={18} />
        </Button>
      </ReportActionToolbar>

      <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
        <SalesTable
          sales={currentSales}
          isLoading={isLoading}
          indexOfFirstItem={indexOfFirstItem}
          onView={handleViewSale}
        />
        <PaginationFooter
          totalItems={sales.length}
          filteredTotal={filteredSales.length}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}
